(function garmentSelectors() {
  const catalog = window.SNF_GARMENT_CATALOG || [];
  const roots = [...document.querySelectorAll('[data-snf-selector]')];
  const claimedProducts = new Set();
  const carrierOptionName = 'SNF Garment';

  roots.forEach((root) => {
    const productKey = root.dataset.snfProductId || root.dataset.snfProductHandle || 'unknown';
    if (root.dataset.snfInitialized === 'true') return;
    if (claimedProducts.has(productKey)) {
      root.hidden = true;
      return;
    }
    claimedProducts.add(productKey);
    root.dataset.snfInitialized = 'true';
    const currency = root.dataset.snfCurrency || 'CAD';
    const productTitle = root.dataset.snfProductTitle || 'this printed design';
    const designButtons = [...root.querySelectorAll('[data-snf-design-value]')];
    const defaultDesign = root.dataset.snfDefaultDesign || designButtons[0]?.dataset.snfDesignValue || '';
    const carrierMode = root.dataset.snfCarrierMode === 'sentinel';
    const embedInProductInfo = root.dataset.snfEmbedInProductInfo === 'true';
    const replaceNativePreview = root.dataset.snfReplaceNativePreview === 'true';
  const categories = [
    { id: 'tshirts', label: 'T-Shirts' },
    { id: 'sweatshirts', label: 'Sweatshirts' },
    { id: 'hoodies', label: 'Hoodies' },
    { id: 'totes', label: root.dataset.snfToteLabel || 'Tote Bags' }
  ];
  const audienceLabels = { adult: 'Adult Unisex', womens: 'Women’s', youth: 'Youth', unisex: 'One size' };
  const sizeOrder = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
  const extendedSizeLabels = new Set(['2XL', '3XL', '4XL', '5XL', '6XL']);
  const visibleColourLimit = 8;
  const preferredColourNames = [
    'Black', 'White', 'Navy', 'Athletic Heather', 'Dark Heather Grey', 'Red', 'Royal', 'Military Green'
  ];

  const state = {
    design: defaultDesign,
    category: 'tshirts',
    audience: 'adult',
    styleId: null,
    colour: 'Black',
    showAllStyles: false,
    showAllColours: false,
    showExtendedSizes: false,
    drafts: {},
    basket: []
  };

  const el = (id) => root.querySelector(`[data-snf-role="${id}"]`);
  const parseJson = (node, fallback) => {
    try {
      return JSON.parse(node?.textContent || '');
    } catch (error) {
      return fallback;
    }
  };
  const productVariants = parseJson(el('product-variants'), []).concat(parseJson(el('bella-product-variants'), []));
  const toteLabels = parseJson(el('tote-labels'), {});
  const categoryTabs = el('category-tabs');
  const audienceTabs = el('audience-tabs');
  const styleOptions = el('style-options');
  const moreStyleOptions = el('more-style-options');
  const colourList = el('colour-list');
  const moreColourOptions = el('more-colour-options');
  const sizeGrid = el('size-grid');
  const moreSizeOptions = el('more-size-options');
  let toastTimer;
  let cartRequestPending = false;

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  function wholesalePricingActive() {
    return document.documentElement.classList.contains('snf-wholesale-active');
  }

  function displayPriceCents(cents, style) {
    const discountPercentage = Number.isFinite(style?.wholesaleDiscountPercentage)
      ? style.wholesaleDiscountPercentage
      : 50;
    return wholesalePricingActive()
      ? Math.round((cents || 0) * (100 - discountPercentage) / 100)
      : (cents || 0);
  }

  function formatMoney(cents, style, alreadyDiscounted = false) {
    const displayCents = alreadyDiscounted ? (cents || 0) : displayPriceCents(cents, style);
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(displayCents / 100);
  }

  function variantOptionValue(variant) {
    return String(variant?.option1 || variant?.options?.[0] || variant?.title || '');
  }

  function variantPriceCents(variant) {
    if (typeof variant?.price === 'number') return Math.round(variant.price);
    const raw = String(variant?.price ?? '').trim();
    if (!raw) return null;
    return raw.includes('.') ? Math.round(Number(raw) * 100) : Math.round(Number(raw));
  }

  function tierForSize(size, style) {
    return style?.sizePriceTiers?.[size] || (extendedSizeLabels.has(size) ? 'extended' : 'regular');
  }

  function tierPrice(style, tier) {
    const matchingPrices = Object.entries(style?.prices || {})
      .filter(([size, price]) => tierForSize(size, style) === tier && Number.isFinite(price))
      .map(([, price]) => price);
    return matchingPrices.length ? matchingPrices[0] : null;
  }

  function carrierVariant(style, tier) {
    const codeToken = `(${String(style?.code || '').toLowerCase()})`;
    return productVariants.find((variant) => {
      const optionValue = variantOptionValue(variant).trim().toLowerCase();
      return optionValue.includes(codeToken) && optionValue.endsWith(`· ${tier}`);
    });
  }

  const requiredCarriers = catalog.filter((style) => !style.pricePending && !style.checkoutPending).flatMap((style) => {
    const tiers = [...new Set(Object.keys(style.prices || {}).map((size) => tierForSize(size, style)))]
      .map((tier) => ({ tier, price: tierPrice(style, tier) }));
    return tiers.map(({ tier, price }) => ({ style, tier, price, variant: carrierVariant(style, tier) }));
  });
  const invalidCarriers = requiredCarriers.filter(({ price, variant }) => (
    !variant
    || !variant.id
    || variant.available === false
    || variantPriceCents(variant) !== price
  ));
  const cartReady = carrierMode && requiredCarriers.length >= 41 && invalidCarriers.length === 0;
  const priceCarrierConfig = {
    version: 1,
    optionName: carrierOptionName,
    ready: cartReady,
    variants: Object.fromEntries(requiredCarriers
      .filter(({ variant }) => variant?.id)
      .map(({ style, tier, price, variant }) => [
        `${style.id}|${tier}`,
        { id: variant.id, price, available: variant.available !== false }
      ]))
  };

  function currentStyle() {
    return catalog.find((item) => item.id === state.styleId) || catalog[0];
  }

  function currentColour(style = currentStyle()) {
    return style?.colours.find((colour) => colour.name === state.colour) || style?.colours[0];
  }

  function styleStartingPriceCents(style) {
    if (style.pricePending) return Number.POSITIVE_INFINITY;
    const prices = Object.values(style.prices || {}).filter(Number.isFinite);
    return prices.length ? displayPriceCents(Math.min(...prices), style) : Number.POSITIVE_INFINITY;
  }

  function stylesFor(category = state.category, audience = state.audience) {
    const styles = catalog.filter((item) => item.category === category && item.audience === audience && !(wholesalePricingActive() && item.wholesalePricePending));
    const defaultHoodieId = { adult: 'atcf2500', youth: 'atcy2500' }[audience];
    if (category === 'hoodies' && defaultHoodieId) {
      styles.sort((a, b) => Number(b.id.startsWith('bc')) - Number(a.id.startsWith('bc')) || Number(b.id === defaultHoodieId) - Number(a.id === defaultHoodieId));
    }
    styles.sort((a, b) => styleStartingPriceCents(a) - styleStartingPriceCents(b));
    return styles;
  }

  function audiencesFor(category = state.category) {
    if (category === 'totes') return ['unisex'];
    return ['adult', 'womens', 'youth'].filter((audience) => catalog.some((item) => item.category === category && item.audience === audience && !(wholesalePricingActive() && item.wholesalePricePending)));
  }

  function ensureSelection() {
    const audiences = audiencesFor();
    if (!audiences.includes(state.audience)) state.audience = audiences[0];
    const styles = stylesFor();
    if (!styles.some((style) => style.id === state.styleId)) state.styleId = styles[0]?.id;
    const style = currentStyle();
    if (!style) return;
    if (!style.colours.some((colour) => colour.name === state.colour)) {
      state.colour = style.colours.some((colour) => colour.name === 'Black') ? 'Black' : style.colours[0]?.name;
    }
  }

  function getDraft(styleId, colour, size) {
    return state.drafts[styleId]?.[colour]?.[size] || 0;
  }

  function hasExtendedDraft(styleId, colour) {
    return [...extendedSizeLabels].some((size) => getDraft(styleId, colour, size) > 0);
  }

  function extendedDraftQuantity(styleId, colour) {
    return [...extendedSizeLabels].reduce((sum, size) => sum + getDraft(styleId, colour, size), 0);
  }

  function setDraft(styleId, colour, size, quantity) {
    state.drafts[styleId] ||= {};
    state.drafts[styleId][colour] ||= {};
    if (quantity > 0) state.drafts[styleId][colour][size] = quantity;
    else delete state.drafts[styleId][colour][size];
  }

  function draftRows(styleId) {
    const style = catalog.find((item) => item.id === styleId);
    const rows = [];
    Object.entries(state.drafts[styleId] || {}).forEach(([colour, sizes]) => {
      Object.entries(sizes).forEach(([size, quantity]) => {
        if (quantity > 0) rows.push({
          styleId,
          colour,
          size,
          quantity,
          price: style?.prices?.[size]
        });
      });
    });
    return rows;
  }

  function allDraftRows() {
    return Object.keys(state.drafts).flatMap((styleId) => draftRows(styleId));
  }

  function draftQuantityForStyle(styleId) {
    return draftRows(styleId).reduce((sum, row) => sum + row.quantity, 0);
  }

  function enteredQuantityLabel(quantity) {
    return `${quantity} garment${quantity === 1 ? '' : 's'} entered`;
  }

  function totalForRows(rows) {
    return rows.reduce((total, row) => {
      total.items += row.quantity;
      if (Number.isFinite(row.price)) {
        total.cents += row.price * row.quantity;
        const style = catalog.find((item) => item.id === row.styleId);
        total.displayCents += displayPriceCents(row.price, style) * row.quantity;
      } else total.tbc += row.quantity;
      return total;
    }, { items: 0, cents: 0, displayCents: 0, tbc: 0 });
  }

  function quantityForColour(styleId, colour) {
    return Object.values(state.drafts[styleId]?.[colour] || {}).reduce((sum, value) => sum + value, 0);
  }

  function normalColourName(value) {
    return String(value || '').toLowerCase().replace(/\*/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function collapsedColoursFor(style) {
    const selected = style.colours.find((colour) => colour.name === state.colour);
    const preferred = preferredColourNames.flatMap((name) => {
      const match = style.colours.find((colour) => normalColourName(colour.name) === normalColourName(name));
      return match ? [match] : [];
    });
    const visible = [];
    const seen = new Set();
    [selected, ...preferred, ...style.colours].filter(Boolean).some((colour) => {
      const key = normalColourName(colour.name);
      if (!seen.has(key)) {
        seen.add(key);
        visible.push(colour);
      }
      return visible.length >= visibleColourLimit;
    });
    return visible;
  }

  function isLight(hex) {
    if (!hex || !/^#[0-9a-f]{6}$/i.test(hex)) return false;
    const red = parseInt(hex.slice(1, 3), 16);
    const green = parseInt(hex.slice(3, 5), 16);
    const blue = parseInt(hex.slice(5, 7), 16);
    return ((red * 299 + green * 587 + blue * 114) / 1000) > 215;
  }

  function styleStartingPrice(style) {
    if (style.pricePending) return 'Retail price to be set';
    const prices = Object.values(style.prices || {}).filter(Number.isFinite);
    if (!prices.length) return 'Price to confirm';
    return `From ${formatMoney(Math.min(...prices), style)}`;
  }

  function renderCurrentStylePrice(style = currentStyle()) {
    const prices = Object.values(style?.prices || {}).filter(Number.isFinite);
    el('current-style-price').textContent = style?.pricePending ? 'Retail price to be set' : prices.length
      ? `${formatMoney(Math.min(...prices), style)} ${currency}`
      : 'Price to confirm';
  }

  function renderDesignOptions() {
    designButtons.forEach((button) => {
      const active = button.dataset.snfDesignValue === state.design;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    });
  }

  function renderCategoryTabs() {
    categoryTabs.innerHTML = categories.map((category) => {
      return `<button class="snf-garment-selector__text-tab${state.category === category.id ? ' is-active' : ''}" type="button" role="tab" aria-selected="${state.category === category.id}" data-category="${category.id}">${category.label}</button>`;
    }).join('');
    categoryTabs.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      state.category = button.dataset.category;
      if (state.category === 'totes') showToteDetails(root.querySelector('[data-snf-tote-tab="description"]'));
      state.audience = audiencesFor()[0];
      state.styleId = stylesFor()[0]?.id;
      state.colour = 'Black';
      state.showAllStyles = false;
      state.showAllColours = false;
      state.showExtendedSizes = false;
      renderAll();
    }));
  }

  function renderAudienceTabs() {
    el('fit-step').hidden = state.category === 'totes';
    const audiences = audiencesFor();
    audienceTabs.innerHTML = audiences.map((audience) => `<button class="snf-garment-selector__text-tab${state.audience === audience ? ' is-active' : ''}" type="button" role="tab" aria-selected="${state.audience === audience}" data-audience="${audience}">${audienceLabels[audience]}</button>`).join('');
    audienceTabs.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      state.audience = button.dataset.audience;
      state.styleId = stylesFor()[0]?.id;
      state.colour = 'Black';
      state.showAllStyles = false;
      state.showAllColours = false;
      state.showExtendedSizes = false;
      renderAll();
    }));
  }

  function renderStyles() {
    const isTote = state.category === 'totes';
    el('style-step').hidden = isTote;
    el('tote-summary').hidden = !isTote;
    const styles = stylesFor();
    const selectedStyle = styles.find((style) => style.id === state.styleId) || styles[0];
    const visibleStyles = state.showAllStyles ? styles : [selectedStyle].filter(Boolean);
    renderCurrentStylePrice(selectedStyle);
    styleOptions.innerHTML = visibleStyles.map((style) => `<button class="snf-garment-selector__style-option${style.id === state.styleId ? ' is-active' : ''}" type="button" role="radio" aria-checked="${style.id === state.styleId}" data-style="${escapeHtml(style.id)}">
      <span class="snf-garment-selector__style-radio" aria-hidden="true"></span>
      <span class="snf-garment-selector__style-copy"><strong>${escapeHtml(style.label)}</strong><small>${escapeHtml(style.code)}</small><span class="snf-garment-selector__style-price">${escapeHtml(styleStartingPrice(style))}</span></span>
    </button>`).join('');
    moreStyleOptions.hidden = styles.length <= 1;
    const hiddenStyleQuantity = state.showAllStyles ? 0 : styles
      .filter((style) => style.id !== selectedStyle?.id)
      .reduce((sum, style) => sum + draftQuantityForStyle(style.id), 0);
    moreStyleOptions.textContent = state.showAllStyles
      ? '− Show selected style only'
      : `+ View other garment styles${hiddenStyleQuantity ? ` (${enteredQuantityLabel(hiddenStyleQuantity)})` : ''}`;
    moreStyleOptions.setAttribute('aria-label', state.showAllStyles
      ? 'Show selected garment style only'
      : `View other garment styles${hiddenStyleQuantity ? ` with ${enteredQuantityLabel(hiddenStyleQuantity)}` : ''}`);
    moreStyleOptions.setAttribute('aria-expanded', String(state.showAllStyles));
    styleOptions.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      state.styleId = button.dataset.style;
      const style = currentStyle();
      state.colour = style.colours.some((colour) => colour.name === 'Black') ? 'Black' : style.colours[0]?.name;
      state.showAllStyles = false;
      state.showAllColours = false;
      state.showExtendedSizes = false;
      renderAll();
    }));
  }

  function renderColourButtons(style) {
    const previousScrollTop = colourList.scrollTop || 0;
    const isTote = style.category === 'totes';
    const colours = state.showAllColours || isTote ? style.colours : collapsedColoursFor(style);
    colourList.innerHTML = colours.map((colour) => {
      const quantity = quantityForColour(style.id, colour.name);
      const imageStyle = colour.image ? `--snf-swatch-image:url(&quot;${escapeHtml(colour.image)}&quot;);` : '';
      const hexStyle = colour.hex ? `--snf-swatch:${escapeHtml(colour.hex)};` : '';
      return `<button class="snf-garment-selector__colour-button${state.colour === colour.name ? ' is-active' : ''}${isLight(colour.hex) ? ' is-light' : ''}" type="button" data-colour="${escapeHtml(colour.name)}" style="${hexStyle}${imageStyle}" title="${escapeHtml(colour.name)}" aria-label="${escapeHtml(colour.name)}${quantity ? `, ${quantity} selected` : ''}" aria-pressed="${state.colour === colour.name}">
        ${colour.isNew ? `<span class="snf-garment-selector__new">${escapeHtml(root.dataset.snfNewColourLabel || 'NEW')}</span>` : ''}
        ${state.colour === colour.name ? '<span class="snf-garment-selector__colour-check" aria-hidden="true">✓</span>' : ''}
        ${quantity ? `<span class="snf-garment-selector__qty-badge" aria-hidden="true">${quantity}</span>` : ''}
      </button>`;
    }).join('');
    if (state.showAllColours) colourList.scrollTop = previousScrollTop;
    const visibleNames = new Set(colours.map((colour) => colour.name));
    const hiddenColourQuantity = state.showAllColours ? 0 : style.colours
      .filter((colour) => !visibleNames.has(colour.name))
      .reduce((sum, colour) => sum + quantityForColour(style.id, colour.name), 0);
    moreColourOptions.hidden = isTote || style.colours.length <= visibleColourLimit;
    moreColourOptions.textContent = state.showAllColours
      ? '− Show fewer colours'
      : `+ View more colours${hiddenColourQuantity ? ` (${enteredQuantityLabel(hiddenColourQuantity)})` : ''}`;
    moreColourOptions.setAttribute('aria-label', state.showAllColours
      ? 'Show fewer garment colours'
      : `View more garment colours${hiddenColourQuantity ? ` with ${enteredQuantityLabel(hiddenColourQuantity)}` : ''}`);
    moreColourOptions.setAttribute('aria-expanded', String(state.showAllColours));
    colourList.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
      state.colour = button.dataset.colour;
      state.showExtendedSizes = hasExtendedDraft(style.id, state.colour);
      renderSelection();
    }));
  }

  function renderSizes(style, colour) {
    const allSizes = [...colour.sizes].sort((a, b) => {
      const aIndex = sizeOrder.indexOf(a);
      const bIndex = sizeOrder.indexOf(b);
      return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
    });
    const hasExtendedSizes = allSizes.some((size) => extendedSizeLabels.has(size));
    const revealExtendedSizes = state.showExtendedSizes;
    const hiddenExtendedQuantity = extendedDraftQuantity(style.id, colour.name);
    const sizes = revealExtendedSizes
      ? allSizes
      : allSizes.filter((size) => !extendedSizeLabels.has(size));
    sizeGrid.innerHTML = sizes.map((size) => {
      const value = getDraft(style.id, colour.name, size);
      const price = style.prices?.[size];
      const inputId = `qty-${style.id}-${size}`.replace(/\s+/g, '-');
      return `<div class="snf-garment-selector__size-cell">
        <label for="${escapeHtml(inputId)}">${escapeHtml(size)}</label>
        <input id="${escapeHtml(inputId)}" type="number" inputmode="numeric" min="0" max="99" step="1" value="${value || ''}" placeholder="0" ${style.pricePending || style.checkoutPending ? 'disabled' : ''} data-size="${escapeHtml(size)}" aria-label="Quantity for ${escapeHtml(colour.name)} in ${escapeHtml(size)}">
        <span class="snf-garment-selector__size-price${Number.isFinite(price) ? '' : ' is-tbc'}">${Number.isFinite(price) ? formatMoney(price, style) : 'Price TBC'}</span>
      </div>`;
    }).join('');
    moreSizeOptions.hidden = !hasExtendedSizes;
    const extendedSizePrice = wholesalePricingActive() ? '$1' : '$2';
    const extendedSizeWords = wholesalePricingActive() ? 'one dollar' : 'two dollars';
    moreSizeOptions.textContent = revealExtendedSizes
      ? '− Hide larger sizes'
      : `+ View larger sizes${hiddenExtendedQuantity ? ` (${enteredQuantityLabel(hiddenExtendedQuantity)})` : ` (+${extendedSizePrice} per garment)`}`;
    moreSizeOptions.setAttribute('aria-label', revealExtendedSizes
      ? 'Hide larger garment sizes'
      : `View larger garment sizes${hiddenExtendedQuantity ? ` with ${enteredQuantityLabel(hiddenExtendedQuantity)}` : `, ${extendedSizeWords} more per garment`}`);
    if ((style.pricePending || style.sizePriceTiers) && !revealExtendedSizes) {
      moreSizeOptions.textContent = '+ View larger sizes';
      moreSizeOptions.setAttribute('aria-label', 'View larger garment sizes');
    }
    moreSizeOptions.setAttribute('aria-expanded', String(revealExtendedSizes));
    sizeGrid.querySelectorAll('input').forEach((input) => input.addEventListener('input', () => {
      const quantity = Math.max(0, Math.min(99, Number.parseInt(input.value || '0', 10) || 0));
      if (String(quantity) !== input.value && input.value !== '') input.value = quantity || '';
      setDraft(style.id, colour.name, input.dataset.size, quantity);
      renderTotals(style);
      renderColourButtons(style);
    }));
  }

  function renderTotals(style) {
    const rows = draftRows(style.id);
    const total = totalForRows(rows);
    const currentQty = quantityForColour(style.id, state.colour);
    el('colour-quantity').textContent = `${currentQty} in this colour`;
    el('style-total-items').textContent = `${total.items} garment${total.items === 1 ? '' : 's'}`;
    el('style-total-price').textContent = total.cents ? formatMoney(total.displayCents, null, true) : '$0.00';
    const tbc = el('style-tbc');
    tbc.hidden = !total.tbc;
    tbc.textContent = total.tbc ? `${total.tbc} garment${total.tbc === 1 ? '' : 's'} need price confirmation` : '';
    const addButton = el('add-style');
    addButton.disabled = total.items === 0 || style.pricePending || style.checkoutPending;
    addButton.textContent = total.items
      ? `Add ${total.items} garment${total.items === 1 ? '' : 's'} to selection`
      : 'Add to selection';
    if (style.pricePending && style.category !== 'totes') {
      el('style-total-price').textContent = 'Retail price to be set';
      addButton.textContent = 'Retail price to be set';
      tbc.hidden = true;
    }
    if (style.checkoutPending && !style.pricePending) {
      el('style-total-price').textContent = styleStartingPrice(style);
      addButton.textContent = 'Preview only';
      tbc.hidden = true;
    }
    if (style.category === 'totes') {
      const noun = total.items === 1 ? toteLabels.single : toteLabels.plural;
      el('style-total-items').textContent = `${total.items} ${noun}`;
      if (style.pricePending) {
        el('style-total-price').textContent = toteLabels.pricePending;
        addButton.textContent = toteLabels.pricePending;
        tbc.hidden = true;
      } else if (total.items) {
        addButton.textContent = toteLabels.addSelection.replace('[count]', total.items).replace('[items]', noun);
      }
    }
  }

  function renderSelection() {
    ensureSelection();
    const style = currentStyle();
    const colour = currentColour(style);
    if (!style || !colour) return;
    const isTote = style.category === 'totes';
    root.classList.toggle('snf-garment-selector--tote', isTote);
    el('tote-details').hidden = !isTote;
    const quantityHeading = el('quantity-heading');
    const quantityHelp = el('quantity-help');
    quantityHeading.dataset.originalText ||= quantityHeading.textContent;
    quantityHelp.dataset.originalText ||= quantityHelp.textContent;
    quantityHeading.textContent = isTote ? toteLabels.quantity : quantityHeading.dataset.originalText;
    quantityHelp.textContent = isTote ? toteLabels.quantityHelp : quantityHelp.dataset.originalText;

    el('selected-colour').textContent = colour.name;

    const notes = [...new Set([style.placement, style.availabilityNote].filter(Boolean))];
    const note = el('selected-placement');
    note.hidden = !notes.length;
    note.textContent = notes.join(' · ');

    const pdf = el('pdf-link');
    const pdfLabel = el('pdf-label');
    pdfLabel.dataset.originalText ||= pdfLabel.textContent;
    pdfLabel.textContent = isTote ? toteLabels.supplierSpecs : pdfLabel.dataset.originalText;
    pdf.hidden = !style.supplierPdf;
    if (style.supplierPdf) pdf.href = style.supplierPdf;

    const details = el('garment-details');
    if (details) {
      details.hidden = !style.description && !style.details?.length;
      if (details.dataset.styleId !== style.id) {
        details.open = false;
        details.dataset.styleId = style.id;
        el('garment-description').textContent = style.description || '';
        el('garment-specs').innerHTML = (style.details || []).map((detail) => `<li>${escapeHtml(detail)}</li>`).join('');
      }
    }

    renderColourButtons(style);
    renderSizes(style, colour);
    renderTotals(style);
  }

  function addCurrentStyle() {
    const style = currentStyle();
    if (style.pricePending || style.checkoutPending) return;
    const rows = draftRows(style.id);
    const design = state.design;
    if (!rows.length) return;

    rows.forEach((row) => {
      const existing = state.basket.find((item) => item.styleId === row.styleId && item.colour === row.colour && item.size === row.size && (item.design || '') === design);
      if (existing) existing.quantity += row.quantity;
      else state.basket.push({ ...row, design });
    });
    delete state.drafts[style.id];
    state.showAllColours = false;
    state.showExtendedSizes = false;
    showToast(`${rows.reduce((sum, row) => sum + row.quantity, 0)} garment${rows.length === 1 && rows[0].quantity === 1 ? '' : 's'} added to your selection.`);
    renderSelection();
    renderBasket();
  }

  function groupedBasket() {
    const groups = new Map();
    state.basket.forEach((row) => {
      const key = `${row.design || ''}\u0000${row.styleId}\u0000${row.colour}`;
      if (!groups.has(key)) groups.set(key, { key, design: row.design || '', styleId: row.styleId, colour: row.colour, rows: [] });
      groups.get(key).rows.push(row);
    });
    return [...groups.values()];
  }

  function renderBasket() {
    const total = totalForRows(state.basket);
    const groups = groupedBasket();
    el('basket-count').textContent = `${total.items} item${total.items === 1 ? '' : 's'}`;
    el('empty-basket').hidden = groups.length > 0;
    el('basket-footer').hidden = groups.length === 0;

    el('basket-lines').innerHTML = groups.map((group, index) => {
      const style = catalog.find((item) => item.id === group.styleId);
      const groupTotal = totalForRows(group.rows);
      const sizes = group.rows
        .sort((a, b) => sizeOrder.indexOf(a.size) - sizeOrder.indexOf(b.size))
        .map((row) => `${row.size} × ${row.quantity}`).join(', ');
      const price = `${groupTotal.cents ? formatMoney(groupTotal.displayCents, null, true) : ''}${groupTotal.cents && groupTotal.tbc ? ' + ' : ''}${groupTotal.tbc ? `${groupTotal.tbc} price TBC` : ''}`;
      return `<div class="snf-garment-selector__basket-line">
        <div><strong>${group.design ? `${escapeHtml(group.design)} · ` : ''}${escapeHtml(style?.label || group.styleId)} · ${escapeHtml(group.colour)}</strong><span>${escapeHtml(audienceLabels[style?.audience] || '')} · ${escapeHtml(style?.code || '')} · ${escapeHtml(sizes)}</span></div>
        <div class="snf-garment-selector__basket-line-price">${escapeHtml(price)}</div>
        <div class="snf-garment-selector__basket-actions">
          <button class="snf-garment-selector__basket-action edit-line" type="button" data-group="${index}" aria-label="Edit ${escapeHtml(style?.label || '')} in ${escapeHtml(group.colour)}">Edit</button>
          <button class="snf-garment-selector__basket-action remove-line" type="button" data-group="${index}" aria-label="Remove ${escapeHtml(style?.label || '')} in ${escapeHtml(group.colour)}">Remove</button>
        </div>
      </div>`;
    }).join('');

    el('basket-total').textContent = `${formatMoney(total.displayCents, null, true)} ${currency}`;
    el('basket-tbc').textContent = total.tbc ? `Plus ${total.tbc} garment${total.tbc === 1 ? '' : 's'} awaiting printed price` : '';

    el('basket-lines').querySelectorAll('.edit-line').forEach((button) => button.addEventListener('click', () => {
      const group = groups[Number(button.dataset.group)];
      const style = catalog.find((item) => item.id === group.styleId);
      if (!style) return;
      group.rows.forEach((row) => setDraft(row.styleId, row.colour, row.size, getDraft(row.styleId, row.colour, row.size) + row.quantity));
      state.basket = state.basket.filter((row) => !(row.styleId === group.styleId && row.colour === group.colour && (row.design || '') === group.design));
      state.design = group.design || state.design;
      state.category = style.category;
      state.audience = style.audience;
      state.styleId = style.id;
      state.colour = group.colour;
      state.showAllStyles = false;
      state.showAllColours = false;
      state.showExtendedSizes = group.rows.some((row) => extendedSizeLabels.has(row.size));
      renderAll();
      root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      showToast('Selection ready to edit. Update the quantities, then add it again.', 4500);
    }));

    el('basket-lines').querySelectorAll('.remove-line').forEach((button) => button.addEventListener('click', () => {
      const group = groups[Number(button.dataset.group)];
      state.basket = state.basket.filter((row) => !(row.styleId === group.styleId && row.colour === group.colour && (row.design || '') === group.design));
      renderBasket();
      showToast('Garment selection removed.');
    }));
  }

  function openReview() {
    const drafts = allDraftRows();
    const draftTotal = totalForRows(drafts);
    if (draftTotal.items) {
      const firstDraft = drafts[0];
      const style = catalog.find((item) => item.id === firstDraft.styleId);
      const currentStyleQuantity = draftQuantityForStyle(firstDraft.styleId);
      const remainingQuantity = draftTotal.items - currentStyleQuantity;
      if (style) {
        state.category = style.category;
        state.audience = style.audience;
        state.styleId = style.id;
        state.colour = firstDraft.colour;
        state.showAllStyles = false;
        state.showAllColours = false;
        state.showExtendedSizes = hasExtendedDraft(style.id, firstDraft.colour);
        renderAll();
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      const warning = remainingQuantity
        ? `Add ${currentStyleQuantity === 1 ? 'this garment' : `these ${currentStyleQuantity} garments`} first. ${remainingQuantity} more garment${remainingQuantity === 1 ? ' is' : 's are'} saved in other styles.`
        : `You still have ${currentStyleQuantity} garment${currentStyleQuantity === 1 ? '' : 's'} not added. Add ${currentStyleQuantity === 1 ? 'it' : 'them'} before reviewing.`;
      showToast(warning, 6500);
      return;
    }
    const groups = groupedBasket();
    const total = totalForRows(state.basket);
    const uniqueStyles = new Set(state.basket.map((row) => row.styleId)).size;
    const uniqueColours = new Set(state.basket.map((row) => `${row.styleId}-${row.colour}`)).size;
    el('dialog-summary').textContent = `${total.items} garment${total.items === 1 ? '' : 's'} across ${uniqueStyles} style${uniqueStyles === 1 ? '' : 's'} and ${uniqueColours} colour selection${uniqueColours === 1 ? '' : 's'}.`;
    el('dialog-lines').innerHTML = groups.map((group) => {
      const style = catalog.find((item) => item.id === group.styleId);
      const sizes = group.rows.map((row) => `${row.size} × ${row.quantity}`).join(', ');
      return `<div class="snf-garment-selector__dialog-line"><strong>${escapeHtml(style?.label || '')} · ${escapeHtml(group.colour)}</strong><span>${escapeHtml(audienceLabels[style?.audience] || '')} · ${escapeHtml(style?.code || '')} · ${escapeHtml(sizes)}</span></div>`;
    }).join('');
    if (cartReady) {
      el('dialog-note').textContent = `These garments will be added to ${productTitle} with the prices shown above.`;
      el('dialog-action').textContent = 'Add all to cart';
    } else if (carrierMode) {
      el('dialog-note').textContent = 'The Shopify price carriers are incomplete or do not match the approved prices. Nothing can be added until the backend setup is corrected.';
      el('dialog-action').textContent = 'Continue editing';
    } else {
      el('dialog-note').textContent = `Preview only — nothing has been added to your Shopify cart for ${productTitle}. The price carriers will be connected after this layout is approved.`;
      el('dialog-action').textContent = 'Continue editing';
    }
    el('review-dialog').showModal();
  }

  function cartItemsForSelection() {
    return state.basket.map((row) => {
      const style = catalog.find((item) => item.id === row.styleId);
      const tier = tierForSize(row.size, style);
      const carrier = priceCarrierConfig.variants[`${row.styleId}|${tier}`];
      if (!style || !carrier?.id || carrier.price !== row.price || carrier.available === false) {
        throw new Error(`Missing matching Shopify price for ${style?.code || row.styleId} ${row.size}.`);
      }
      const properties = {
        Design: row.design ? `${productTitle} · ${row.design}` : productTitle,
        'Design choice': row.design || '',
        Garment: `${audienceLabels[style.audience] || ''} · ${style.label}`,
        'Style code': style.code,
        Colour: row.colour,
        Size: row.size,
        '_Design product ID': root.dataset.snfProductGid || root.dataset.snfProductId || '',
        '_Design product handle': root.dataset.snfProductHandle || '',
        _snf_order_group: 'garment',
        '_SNF selection key': [
          root.dataset.snfProductGid || root.dataset.snfProductId || root.dataset.snfProductHandle || '',
          row.design || '',
          style.id,
          row.colour,
          row.size
        ].join('|'),
        '_SNF catalog version': 'v1'
      };
      if (root.dataset.snfProductImage) {
        properties['_Design image'] = root.dataset.snfProductImage;
        properties['_Design image alt'] = root.dataset.snfProductImageAlt || productTitle;
      }
      if (style.placement) properties['Print placement'] = style.placement;
      if (!window.SNFShippingCarriers) throw new Error('Please refresh the page before adding garments.');
      return { id: window.SNFShippingCarriers.variantFor(carrier.id, wholesalePricingActive()), quantity: row.quantity, properties };
    });
  }

  function localizedCartUrl(path) {
    const shopRoot = window.Shopify?.routes?.root || '/';
    return `${shopRoot.replace(/\/?$/, '/')}${String(path || '').replace(/^\//, '')}`;
  }

  async function fetchCartSnapshot() {
    const response = await fetch(localizedCartUrl('cart.js'), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok || !Array.isArray(cart.items)) {
      throw new Error(cart.description || cart.message || 'The shopping cart could not be checked. Please try again.');
    }
    return cart;
  }

  function requestedItemSignature(item) {
    const selectionKey = item.properties?.['_SNF selection key'];
    if (selectionKey) return `${String(item.id)}\u0000${String(selectionKey)}`;
    const properties = Object.entries(item.properties || {})
      .sort(([left], [right]) => left.localeCompare(right));
    return `${String(item.id)}\u0000${JSON.stringify(properties)}`;
  }

  function cartLineMatchesRequest(line, item) {
    const lineVariantId = line?.variant_id ?? line?.id;
    if (String(lineVariantId) !== String(item.id)) return false;
    const selectionKey = item.properties?.['_SNF selection key'];
    if (selectionKey) {
      return String(line.properties?.['_SNF selection key'] || '') === String(selectionKey);
    }
    return Object.entries(item.properties || {}).every(([name, value]) => (
      String(line.properties?.[name] ?? '') === String(value ?? '')
    ));
  }

  function cartQuantityForRequest(cart, item) {
    return (cart.items || []).reduce((quantity, line) => (
      cartLineMatchesRequest(line, item) ? quantity + (Number(line.quantity) || 0) : quantity
    ), 0);
  }

  function findMissingCartItems(beforeCart, afterCart, items) {
    return items.map((item) => {
      const expectedQuantity = cartQuantityForRequest(beforeCart, item) + item.quantity;
      const actualQuantity = cartQuantityForRequest(afterCart, item);
      const missingQuantity = Math.max(0, expectedQuantity - actualQuantity);
      return missingQuantity ? { ...item, quantity: missingQuantity } : null;
    }).filter(Boolean);
  }

  function retainMissingBasketRows(items, missingItems) {
    const remainingBySignature = new Map(missingItems.map((item) => [
      requestedItemSignature(item),
      item.quantity
    ]));
    state.basket = state.basket.map((row, index) => {
      const signature = requestedItemSignature(items[index]);
      const remainingQuantity = remainingBySignature.get(signature) || 0;
      return remainingQuantity ? { ...row, quantity: remainingQuantity } : null;
    }).filter(Boolean);
  }

  async function submitCartItems(items, sections, singleItem = false) {
    const body = singleItem
      ? { id: items[0].id, quantity: items[0].quantity, properties: items[0].properties }
      : { items };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    const baseUrl = root.dataset.snfCartAddUrl || localizedCartUrl('cart/add');
    const cartAddUrl = /\.js(?:\?|$)/.test(baseUrl) ? baseUrl : `${baseUrl}.js`;
    const response = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || Number(result.status) >= 400) {
      const error = new Error(result.description || result.message || 'Shopify could not add the garments to the cart.');
      error.httpStatus = Number(result.status) || response.status;
      throw error;
    }
    return result;
  }

  function cartUiState(result, cart, items) {
    if (!result?.sections) return null;
    const responseItems = Array.isArray(result.items) ? result.items : [result];
    let line = null;
    for (let index = responseItems.length - 1; index >= 0; index -= 1) {
      if (responseItems[index]?.key) {
        line = responseItems[index];
        break;
      }
    }
    if (!line) {
      for (let index = items.length - 1; index >= 0; index -= 1) {
        line = (cart.items || []).find((cartLine) => cartLineMatchesRequest(cartLine, items[index]));
        if (line) break;
      }
    }
    if (!line) return null;
    return { ...line, sections: result.sections };
  }

  async function addSelectionToCart() {
    if (!cartReady || cartRequestPending || !state.basket.length) return;
    let items;
    try {
      items = cartItemsForSelection();
    } catch (error) {
      showToast(error.message || 'A matching Shopify price could not be found.', 6500);
      return;
    }

    const action = el('dialog-action');
    const previousText = action.textContent;
    cartRequestPending = true;
    action.disabled = true;
    action.textContent = 'Adding…';

    try {
      const cartUi = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      let sections = [];
      if (cartUi && typeof cartUi.getSectionsToRender === 'function') {
        sections = cartUi.getSectionsToRender().map((section) => section.id).filter(Boolean);
      }
      const requestedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      await window.SNFShippingCarriers.normalize(wholesalePricingActive());
      const beforeCart = await fetchCartSnapshot();
      let lastResult = null;
      let initialError = null;

      try {
        lastResult = await submitCartItems(items, sections);
      } catch (error) {
        initialError = error;
      }

      let verifiedCart = await fetchCartSnapshot();
      let missingItems = findMissingCartItems(beforeCart, verifiedCart, items);

      if (missingItems.length && initialError?.httpStatus !== 422) {
        action.textContent = 'Confirming cart…';
        for (const missingItem of missingItems) {
          try {
            lastResult = await submitCartItems([missingItem], sections, true);
          } catch (error) {
            initialError ||= error;
          }
        }
        verifiedCart = await fetchCartSnapshot();
        missingItems = findMissingCartItems(beforeCart, verifiedCart, items);
      }

      if (missingItems.length) {
        retainMissingBasketRows(items, missingItems);
        renderBasket();
        el('review-dialog').close();
        const missingQuantity = missingItems.reduce((sum, item) => sum + item.quantity, 0);
        const addedQuantity = requestedQuantity - missingQuantity;
        if (addedQuantity > 0) {
          throw new Error(`${addedQuantity} garment${addedQuantity === 1 ? '' : 's'} added. ${missingQuantity} could not be added and ${missingQuantity === 1 ? 'remains' : 'remain'} in your selection.`);
        }
        throw initialError || new Error('The garments could not be added. Your selection has been kept so you can try again.');
      }

      state.basket = [];
      renderBasket();
      el('review-dialog').close();
      if (items.length > 1) {
        window.location.assign(localizedCartUrl('cart'));
        return;
      }
      const uiState = cartUiState(lastResult, verifiedCart, items);
      if (cartUi && typeof cartUi.renderContents === 'function' && uiState) {
        try {
          cartUi.renderContents(uiState);
          showToast(`All ${requestedQuantity} garment${requestedQuantity === 1 ? '' : 's'} added to the cart.`, 4500);
          return;
        } catch (error) {
          console.error('The cart was updated, but its preview could not refresh.', error);
        }
      }
      window.location.assign(localizedCartUrl('cart'));
    } catch (error) {
      showToast(error.message || 'Shopify could not add the garments to the cart.', 6500);
    } finally {
      cartRequestPending = false;
      action.disabled = false;
      action.textContent = previousText;
    }
  }

  function productInfoContainers() {
    const productSection = document.querySelector('section[id^="MainProduct-"]') || document.querySelector('.product');
    return productSection
      ? [...productSection.querySelectorAll('.product__info-container')]
      : [...document.querySelectorAll('.product__info-container')];
  }

  function visibleProductInfo(containers) {
    return containers.find((container) => {
      const styles = window.getComputedStyle(container);
      return styles.display !== 'none'
        && styles.visibility !== 'hidden'
        && container.getClientRects().length > 0;
    }) || containers[0];
  }

  function embedSelector(productInfo) {
    if (!embedInProductInfo || !productInfo) return;
    const titleBlock = productInfo.querySelector('.product__title');
    if (titleBlock) titleBlock.insertAdjacentElement('afterend', root);
    else productInfo.prepend(root);
    productInfo.classList.remove('product__column-sticky');
    root.classList.remove('page-width');
    root.classList.add('snf-garment-selector--embedded');
  }

  function hideNativePurchaseControls(productInfo) {
    productInfo.classList.add('snf-garment-selector-replaces-native');
    const nativeControls = productInfo.querySelectorAll([
        '[id^="price-"]',
        '[id^="Quantity-Form-"]',
        'variant-selects',
        'variant-radios',
        '.product__tax',
        'product-form',
        'pickup-availability',
        '.product__pickup-availabilities',
        'form[id^="product-form-installment-"]',
        '.shopify-payment-button',
        '[data-shopify="payment-button"]',
        '[data-shopify-editor-block*="afterpay-on-site-messaging"]'
      ].join(', '));
    nativeControls.forEach((node) => {
      const target = node.matches('form[id^="product-form-installment-"]')
        ? node.closest('[data-shopify-editor-block]') || node
        : node;
      target.hidden = true;
      target.style.display = 'none';
      target.setAttribute('aria-hidden', 'true');
    });
  }

  function applyPurchaseMode() {
    const productInfos = productInfoContainers();
    const productInfo = visibleProductInfo(productInfos);

    embedSelector(productInfo);

    if (cartReady || replaceNativePreview) {
      productInfos.forEach(hideNativePurchaseControls);
    }

    if (cartReady) {
      el('preview-notice')?.remove();
      el('connection-note').textContent = 'Your garment choices will be added together with the correct website prices.';
    } else if (carrierMode) {
      el('connection-note').textContent = 'This product’s Shopify price setup is incomplete. Its original purchase controls remain available while the selector is repaired.';
    }
  }

  let responsivePlacementFrame;
  function refreshResponsivePlacement() {
    window.cancelAnimationFrame(responsivePlacementFrame);
    responsivePlacementFrame = window.requestAnimationFrame(applyPurchaseMode);
  }

  function showToast(message, duration = 2800) {
    const toast = el('toast');
    clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add('is-visible');
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), duration);
  }

  function renderAll() {
    ensureSelection();
    renderDesignOptions();
    renderCategoryTabs();
    renderAudienceTabs();
    renderStyles();
    renderSelection();
    renderBasket();
  }

  designButtons.forEach((button) => button.addEventListener('click', () => {
    state.design = button.dataset.snfDesignValue || '';
    renderDesignOptions();
  }));
  el('add-style').addEventListener('click', addCurrentStyle);
  el('review-order').addEventListener('click', openReview);
  moreStyleOptions.addEventListener('click', (event) => {
    event.preventDefault();
    state.showAllStyles = !state.showAllStyles;
    renderStyles();
  });
  moreColourOptions.addEventListener('click', (event) => {
    event.preventDefault();
    state.showAllColours = !state.showAllColours;
    renderSelection();
  });
  moreSizeOptions.addEventListener('click', (event) => {
    event.preventDefault();
    state.showExtendedSizes = !state.showExtendedSizes;
    renderSelection();
  });
  el('close-dialog').addEventListener('click', () => el('review-dialog').close());
  el('dialog-action').addEventListener('click', () => {
    if (cartReady) addSelectionToCart();
    else el('review-dialog').close();
  });
  el('review-dialog').addEventListener('click', (event) => {
    if (event.target === el('review-dialog')) el('review-dialog').close();
  });

  const toteTabs = [...root.querySelectorAll('[data-snf-tote-tab]')];
  function showToteDetails(button) {
    toteTabs.forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      el(`tote-${tab.dataset.snfToteTab}`).hidden = !active;
    });
  }
  toteTabs.forEach((button, index) => {
    button.addEventListener('click', () => showToteDetails(button));
    button.addEventListener('keydown', (event) => {
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % toteTabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (index + toteTabs.length - 1) % toteTabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = toteTabs.length - 1;
      else return;
      event.preventDefault();
      showToteDetails(toteTabs[nextIndex]);
      toteTabs[nextIndex].focus();
    });
  });

  if (!catalog.length) {
    el('selection-panel').innerHTML = '<p>Garment catalogue could not be loaded.</p>';
    return;
  }
  root.snfGarmentSelector = {
    cartEligible: carrierMode,
    cartReady,
    carrierConfig: priceCarrierConfig,
    productVariants,
    getSelection() {
      return state.basket.map((row) => {
        const style = catalog.find((item) => item.id === row.styleId);
        const variantMap = priceCarrierConfig.variants || {};
        const mapped = variantMap[`${row.styleId}|${tierForSize(row.size, style)}`];
        const variantId = typeof mapped === 'object' ? mapped?.id : mapped;
        return {
          variantId: variantId || null,
          quantity: row.quantity,
          expectedUnitPrice: row.price,
          properties: {
            Design: row.design ? `${productTitle} · ${row.design}` : productTitle,
            'Design choice': row.design || '',
            'Garment style': style?.label || row.styleId,
            'Style code': style?.code || '',
            Fit: audienceLabels[style?.audience] || '',
            Colour: row.colour,
            Size: row.size,
            '_Design product ID': root.dataset.snfProductId || '',
            '_Design product handle': root.dataset.snfProductHandle || '',
            '_Design image': root.dataset.snfProductImage || '',
            '_Design image alt': root.dataset.snfProductImageAlt || productTitle
          }
        };
      });
    }
  };
  window.addEventListener('resize', refreshResponsivePlacement, { passive: true });
  document.addEventListener('shopify:section:load', refreshResponsivePlacement);
  document.addEventListener('snf:wholesale-change', renderAll);
  applyPurchaseMode();
  renderAll();
  });
})();


